import React from 'react';
import { Result, Button } from 'antd';
import { useNavigate } from 'react-router-dom';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} />;
    }
    return this.props.children;
  }
}

const ErrorFallback: React.FC<{ error: Error | null }> = ({ error }) => {
  const navigate = useNavigate();

  return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <Result
        status="error"
        title="Something went wrong"
        subTitle={error?.message || 'An unexpected error occurred'}
        extra={[
          <Button type="primary" key="home" onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </Button>,
          <Button key="reload" onClick={() => window.location.reload()}>
            Reload Page
          </Button>,
        ]}
      />
    </div>
  );
};

const ErrorFallbackWrapper: React.FC<{ error: Error | null }> = ({ error }) => {
  const navigate = React.useMemo(
    () => (path: string) => window.location.href = path,
    []
  );

  return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <Result
        status="error"
        title="Something went wrong"
        subTitle={error?.message || 'An unexpected error occurred'}
        extra={
          <Button type="primary" onClick={() => window.location.href = '/dashboard'}>
            Go to Dashboard
          </Button>
        }
      />
    </div>
  );
};

export default ErrorBoundary;
